import React, { useState, useEffect, useRef } from 'react';
import api from '../api';

const containerStyle = {
  padding: '1.5rem',
  backgroundColor: '#f8f9fa',
  minHeight: '100vh'
};

const headerStyle = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: '2rem',
  borderRadius: '16px',
  textAlign: 'center',
  marginBottom: '2rem',
  boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)'
};

const formContainerStyle = {
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: '2rem',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  border: '1px solid #e9ecef',
  marginBottom: '2rem'
};

const tableContainerStyle = {
  backgroundColor: 'white',
  borderRadius: '16px',
  overflowX: 'auto',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  border: '1px solid #e9ecef',
  marginBottom: '1rem'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse'
};

const thStyle = {
  border: '1px solid #ddd',
  padding: '1rem',
  textAlign: 'center',
  backgroundColor: '#f8f9fa',
  color: '#333',
  fontWeight: 'bold'
};

const tdStyle = {
  border: '1px solid #ddd',
  padding: '1rem',
  textAlign: 'center',
  verticalAlign: 'middle'
};

const textTdStyle = {
  ...tdStyle,
  maxWidth: '150px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const inputStyle = {
  padding: '0.5rem',
  borderRadius: '6px',
  border: '1px solid #ddd',
  backgroundColor: 'white',
  marginRight: '1rem'
};

const buttonStyle = {
  backgroundColor: '#28a745',
  color: 'white',
  border: 'none',
  padding: '0.75rem 1rem',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: '600',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)'
};

const statsStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '2rem',
  marginBottom: '2rem'
};

const statCardStyle = {
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: '2rem',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  border: '1px solid #e9ecef',
  flex: '1 1 180px',
  textAlign: 'center'
};

const AdminFinance = () => {
  const [activeTab, setActiveTab] = useState('payments');
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [users, setUsers] = useState([]);
  const [editingRecord, setEditingRecord] = useState(null);
  const [openOptions, setOpenOptions] = useState(null);
  const [formData, setFormData] = useState({
    userId: '',
    amount: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [expenseFormData, setExpenseFormData] = useState({
    reason: '',
    amount: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [settlementFormData, setSettlementFormData] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [message, setMessage] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const userSelectRef = useRef(null);

  const fetchPayments = async () => {
    try {
      const res = await api.get('/admin/payments');
      setPayments(res.data);
    } catch (err) {
      console.error('Fetch payments error:', err);
    }
  };

  const fetchExpenses = async () => {
    try {
      const res = await api.get('/admin/expenses');
      setExpenses(res.data);
    } catch (err) {
      console.error('Fetch expenses error:', err);
    }
  };

  const fetchSettlements = async () => {
    try {
      const res = await api.get('/admin/settlements');
      setSettlements(res.data);
    } catch (err) {
      console.error('Fetch settlements error:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data);
    } catch (err) {
      console.error('Fetch users error:', err);
    }
  };

  useEffect(() => {
    fetchPayments();
    fetchExpenses();
    fetchSettlements();
    fetchUsers();
  }, []);

  useEffect(() => {
    const filtered = searchTerm 
      ? users.filter(u => 
          u.phone?.toLowerCase().includes(searchTerm.toLowerCase()) || 
          (u.email && u.email.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      : users;
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (userSelectRef.current && !userSelectRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.userId) {
      setMessage('Please select a user.');
      return;
    }
    try {
      await api.post('/admin/payments', formData);
      setMessage('Payment added successfully.');
      setFormData({ userId: '', amount: '', date: new Date().toISOString().split('T')[0] });
      setSearchTerm('');
      setShowDropdown(false);
      fetchPayments();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to add payment.');
    }
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!expenseFormData.reason || !expenseFormData.amount) {
      setMessage('Please provide reason and amount.');
      return;
    }
    try {
      await api.post('/admin/expenses', expenseFormData);
      setMessage('Expense added successfully.');
      setExpenseFormData({ reason: '', amount: '', date: new Date().toISOString().split('T')[0] });
      fetchExpenses();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to add expense.');
    }
  };

  const handleExpenseInputChange = (e) => {
    setExpenseFormData({ ...expenseFormData, [e.target.name]: e.target.value });
  };

  const handleSettlementSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/settlements', settlementFormData);
      setMessage('Settlement added successfully.');
      setSettlementFormData({ amount: '', date: new Date().toISOString().split('T')[0] });
      fetchSettlements();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to add settlement.');
    }
  };

  const startEditing = (type, record) => {
    setEditingRecord({
      type,
      id: record.id,
      reason: record.reason || '',
      amount: String(record.amount),
      date: String(record.date).slice(0, 10)
    });
  };

  const handleEditSave = async () => {
    const { type, id, ...record } = editingRecord;
    try {
      await api.put(`/admin/${type}/${id}`, record);
      setMessage(`${type === 'expenses' ? 'Expenditure' : type.slice(0, -1)} updated successfully.`);
      setEditingRecord(null);
      if (type === 'payments') fetchPayments();
      if (type === 'expenses') fetchExpenses();
      if (type === 'settlements') fetchSettlements();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to update record.');
    }
  };

  const handleDeleteRecord = async (type, record) => {
    const recordName = type === 'expenses' ? 'expenditure' : type.slice(0, -1);
    if (!window.confirm(`Delete this ${recordName}? This action cannot be undone.`)) return;
    try {
      await api.delete(`/admin/${type}/${record.id}`);
      setMessage(`${recordName[0].toUpperCase()}${recordName.slice(1)} deleted successfully.`);
      setOpenOptions(null);
      if (type === 'payments') fetchPayments();
      if (type === 'expenses') fetchExpenses();
      if (type === 'settlements') fetchSettlements();
    } catch (err) {
      setMessage(err.response?.data?.message || `Failed to delete ${recordName}.`);
    }
  };

  const renderRecordOptions = (type, record) => {
    const isOpen = openOptions?.type === type && openOptions.id === record.id;
    return (
      <div style={{ display: 'inline-block', position: 'relative' }}>
        <button
          type="button"
          aria-label={`Options for ${type.slice(0, -1)}`}
          title="Options"
          onClick={() => setOpenOptions(isOpen ? null : { type, id: record.id })}
          style={{
            background: 'transparent',
            border: '1px solid #ced4da',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1.4rem',
            lineHeight: 1,
            padding: '0.3rem 0.6rem'
          }}
        >
          ⋮
        </button>
        {isOpen && (
          <div role="menu" style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            zIndex: 10,
            minWidth: '120px',
            padding: '0.25rem',
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                startEditing(type, record);
                setOpenOptions(null);
              }}
              style={{ display: 'block', width: '100%', padding: '0.5rem', border: 0, background: 'white', textAlign: 'left', cursor: 'pointer' }}
            >
              Edit
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => handleDeleteRecord(type, record)}
              style={{ display: 'block', width: '100%', padding: '0.5rem', border: 0, background: 'white', color: '#dc3545', textAlign: 'left', cursor: 'pointer' }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    );
  };

  const totalCollected = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const totalSpent = expenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
  const totalSettled = settlements.reduce((sum, settlement) => sum + parseFloat(settlement.amount || 0), 0);
  const totalProfit = totalCollected - totalSpent;
  const inAccount = totalProfit - totalSettled;

  // Simple retention: count unique users with multiple payments
  const userPaymentCounts = {};
  payments.forEach(p => {
    userPaymentCounts[p.userId] = (userPaymentCounts[p.userId] || 0) + 1;
  });
  const repeatUsers = Object.values(userPaymentCounts).filter(count => count > 1).length;
  const retentionRate = payments.length > 0 ? ((repeatUsers / new Set(payments.map(p => p.userId)).size) * 100).toFixed(1) : 0;

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2 style={{ margin: 0, fontSize: '2rem' }}>Finance Management</h2>
      </div>
      {message && <div style={{ backgroundColor: '#d4edda', border: '1px solid #c3e6cb', color: '#155724', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>{message}</div>}
      
      <div style={statsStyle}>
        <div style={statCardStyle}>
          <h3>Total Collected</h3>
          <p style={{ fontSize: '2rem', color: '#28a745', fontWeight: 'bold' }}>₹{totalCollected.toFixed(2)}</p>
        </div>
        <div style={statCardStyle}>
          <h3>Total Spent</h3>
          <p style={{ fontSize: '2rem', color: '#dc3545', fontWeight: 'bold' }}>₹{totalSpent.toFixed(2)}</p>
        </div>
        <div style={statCardStyle}>
          <h3>Total Profit</h3>
          <p style={{ fontSize: '2rem', color: totalProfit >= 0 ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>₹{totalProfit.toFixed(2)}</p>
        </div>
        <div style={statCardStyle}>
          <h3>Settlement</h3>
          <p style={{ fontSize: '2rem', color: '#007bff', fontWeight: 'bold' }}>₹{totalSettled.toFixed(2)}</p>
        </div>
        <div style={statCardStyle}>
          <h3>In Account</h3>
          <p style={{ fontSize: '2rem', color: inAccount >= 0 ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>₹{inAccount.toFixed(2)}</p>
        </div>
        <div style={statCardStyle}>
          <h3>Repeat Users</h3>
          <p style={{ fontSize: '2rem', color: '#007bff', fontWeight: 'bold' }}>{repeatUsers}</p>
        </div>
        <div style={statCardStyle}>
          <h3>Retention Rate</h3>
          <p style={{ fontSize: '2rem', color: '#28a745', fontWeight: 'bold' }}>{retentionRate}%</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
        <button
          onClick={() => setActiveTab('payments')}
          style={{
            ...buttonStyle,
            backgroundColor: activeTab === 'payments' ? '#007bff' : '#6c757d',
            flex: 1,
            padding: '1rem'
          }}
        >
          Payments
        </button>
        <button
          onClick={() => setActiveTab('expenses')}
          style={{
            ...buttonStyle,
            backgroundColor: activeTab === 'expenses' ? '#007bff' : '#6c757d',
            flex: 1,
            padding: '1rem'
          }}
        >
          Expenditures
        </button>
        <button
          onClick={() => setActiveTab('settlements')}
          style={{
            ...buttonStyle,
            backgroundColor: activeTab === 'settlements' ? '#007bff' : '#6c757d',
            flex: 1,
            padding: '1rem'
          }}
        >
          Settlement
        </button>
      </div>

      {activeTab === 'payments' ? (
        <div style={formContainerStyle}>
          <h3>Add Payment</h3>
          <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'end' }}>
            <div ref={userSelectRef} style={{ position: 'relative', minWidth: '250px' }}>
              <input
                type="text"
                placeholder="Search user by phone or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setShowDropdown(true)}
                style={{ ...inputStyle, width: '100%' }}
              />
              {showDropdown && filteredUsers.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderTop: 'none',
                  borderRadius: '0 0 8px 8px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }}>
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                    {filteredUsers.map(u => (
                      <li
                        key={u.id}
                        onClick={() => {
                          setFormData(prev => ({ ...prev, userId: u.id }));
                          setSearchTerm(`${u.phone} - ${u.email}`);
                          setShowDropdown(false);
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          borderBottom: '1px solid #f0f0f0'
                        }}
                        onMouseEnter={e => e.target.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={e => e.target.style.backgroundColor = 'white'}
                      >
                        <span style={{ fontWeight: '500' }}>{u.phone}</span>
                        <span style={{ fontSize: '0.875rem', color: '#666' }}>{u.email}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {showDropdown && filteredUsers.length === 0 && searchTerm && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderTop: 'none',
                  borderRadius: '0 0 8px 8px',
                  padding: '1rem',
                  textAlign: 'center',
                  color: '#999',
                  zIndex: 1000
                }}>
                  No users found
                </div>
              )}
            </div>
            <input
              type="number"
              name="amount"
              placeholder="Amount (₹)"
              value={formData.amount}
              onChange={handleInputChange}
              style={{ ...inputStyle, width: '150px' }}
              required
            />
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              style={inputStyle}
            />
            <button type="submit" style={buttonStyle}>Add Payment</button>
          </form>
        </div>
      ) : activeTab === 'expenses' ? (
        <div style={formContainerStyle}>
          <h3>Add Expenditure</h3>
          <form onSubmit={handleExpenseSubmit} style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'end' }}>
            <input
              type="text"
              name="reason"
              placeholder="Reason for expenditure"
              value={expenseFormData.reason}
              onChange={handleExpenseInputChange}
              style={{ ...inputStyle, width: '250px' }}
              required
            />
            <input
              type="number"
              name="amount"
              placeholder="Amount (₹)"
              value={expenseFormData.amount}
              onChange={handleExpenseInputChange}
              style={{ ...inputStyle, width: '150px' }}
              required
            />
            <input
              type="date"
              name="date"
              value={expenseFormData.date}
              onChange={handleExpenseInputChange}
              style={inputStyle}
            />
            <button type="submit" style={buttonStyle}>Add Expense</button>
          </form>
        </div>
      ) : (
        <div style={formContainerStyle}>
          <h3>Add Settlement</h3>
          <form onSubmit={handleSettlementSubmit} style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'end' }}>
            <input
              type="number"
              min="0.01"
              step="0.01"
              name="amount"
              placeholder="Amount (₹)"
              value={settlementFormData.amount}
              onChange={e => setSettlementFormData({ ...settlementFormData, amount: e.target.value })}
              style={{ ...inputStyle, width: '150px' }}
              required
            />
            <input
              type="date"
              name="date"
              value={settlementFormData.date}
              onChange={e => setSettlementFormData({ ...settlementFormData, date: e.target.value })}
              style={inputStyle}
              required
            />
            <button type="submit" style={buttonStyle}>Add Settlement</button>
          </form>
        </div>
      )}

      {activeTab === 'payments' ? (
        <div style={tableContainerStyle}>
          <h3 style={{ padding: '1rem', margin: 0, backgroundColor: '#f8f9fa', borderBottom: '1px solid #ddd' }}>Payments</h3>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>User</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(p => (
                <tr key={p.id}>
                  <td style={textTdStyle}>
                    {users.find(u => u.id === p.userId)?.phone || 'Unknown'}
                  </td>
                  <td style={tdStyle}>{editingRecord?.type === 'payments' && editingRecord.id === p.id ? (
                    <input type="number" min="0.01" step="0.01" value={editingRecord.amount} onChange={e => setEditingRecord({ ...editingRecord, amount: e.target.value })} style={{ ...inputStyle, width: '110px', margin: 0 }} />
                  ) : `₹${parseFloat(p.amount).toFixed(2)}`}</td>
                  <td style={tdStyle}>{editingRecord?.type === 'payments' && editingRecord.id === p.id ? (
                    <input type="date" value={editingRecord.date} onChange={e => setEditingRecord({ ...editingRecord, date: e.target.value })} style={{ ...inputStyle, margin: 0 }} />
                  ) : new Date(p.date).toLocaleDateString()}</td>
                  <td style={tdStyle}>
                    {editingRecord?.type === 'payments' && editingRecord.id === p.id ? (
                      <>
                        <button type="button" onClick={handleEditSave} style={buttonStyle}>Save</button>{' '}
                        <button type="button" onClick={() => setEditingRecord(null)} style={{ ...buttonStyle, backgroundColor: '#6c757d' }}>Cancel</button>
                      </>
                    ) : renderRecordOptions('payments', p)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : activeTab === 'expenses' ? (
        <div style={tableContainerStyle}>
          <h3 style={{ padding: '1rem', margin: 0, backgroundColor: '#f8f9fa', borderBottom: '1px solid #ddd' }}>Expenditures</h3>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Reason</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map(e => (
                <tr key={e.id}>
                  <td style={textTdStyle}>{editingRecord?.type === 'expenses' && editingRecord.id === e.id ? (
                    <input type="text" value={editingRecord.reason} onChange={event => setEditingRecord({ ...editingRecord, reason: event.target.value })} style={{ ...inputStyle, margin: 0, width: '100%' }} />
                  ) : e.reason}</td>
                  <td style={tdStyle}>{editingRecord?.type === 'expenses' && editingRecord.id === e.id ? (
                    <input type="number" min="0.01" step="0.01" value={editingRecord.amount} onChange={event => setEditingRecord({ ...editingRecord, amount: event.target.value })} style={{ ...inputStyle, width: '110px', margin: 0 }} />
                  ) : `₹${parseFloat(e.amount).toFixed(2)}`}</td>
                  <td style={tdStyle}>{editingRecord?.type === 'expenses' && editingRecord.id === e.id ? (
                    <input type="date" value={editingRecord.date} onChange={event => setEditingRecord({ ...editingRecord, date: event.target.value })} style={{ ...inputStyle, margin: 0 }} />
                  ) : new Date(e.date).toLocaleDateString()}</td>
                  <td style={tdStyle}>
                    {editingRecord?.type === 'expenses' && editingRecord.id === e.id ? (
                      <>
                        <button type="button" onClick={handleEditSave} style={buttonStyle}>Save</button>{' '}
                        <button type="button" onClick={() => setEditingRecord(null)} style={{ ...buttonStyle, backgroundColor: '#6c757d' }}>Cancel</button>
                      </>
                    ) : renderRecordOptions('expenses', e)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={tableContainerStyle}>
          <h3 style={{ padding: '1rem', margin: 0, backgroundColor: '#f8f9fa', borderBottom: '1px solid #ddd' }}>Settlements</h3>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {settlements.map(settlement => (
                <tr key={settlement.id}>
                  <td style={tdStyle}>{editingRecord?.type === 'settlements' && editingRecord.id === settlement.id ? (
                    <input type="number" min="0.01" step="0.01" value={editingRecord.amount} onChange={e => setEditingRecord({ ...editingRecord, amount: e.target.value })} style={{ ...inputStyle, width: '110px', margin: 0 }} />
                  ) : `₹${parseFloat(settlement.amount).toFixed(2)}`}</td>
                  <td style={tdStyle}>{editingRecord?.type === 'settlements' && editingRecord.id === settlement.id ? (
                    <input type="date" value={editingRecord.date} onChange={e => setEditingRecord({ ...editingRecord, date: e.target.value })} style={{ ...inputStyle, margin: 0 }} />
                  ) : new Date(settlement.date).toLocaleDateString()}</td>
                  <td style={tdStyle}>
                    {editingRecord?.type === 'settlements' && editingRecord.id === settlement.id ? (
                      <>
                        <button type="button" onClick={handleEditSave} style={buttonStyle}>Save</button>{' '}
                        <button type="button" onClick={() => setEditingRecord(null)} style={{ ...buttonStyle, backgroundColor: '#6c757d' }}>Cancel</button>
                      </>
                    ) : renderRecordOptions('settlements', settlement)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminFinance;
